import React from "react";
import { Button, Card, Form, Input, Typography, ConfigProvider, Checkbox } from "antd";
import { UserOutlined, LockOutlined } from "@ant-design/icons";
import { Formik } from "formik";
import * as yup from "yup";
import { useNavigate, Link } from "react-router-dom";
import Swal from "sweetalert2";
import { useAuth } from "../AuthProvider";

const { Title, Text } = Typography;

const loginSchema = yup.object().shape({
  Email: yup.string().email("Please enter a valid email").required("Please enter your email"),
  Password: yup.string().required("Please enter your password"),
});

const Login = () => {
  const navigate = useNavigate();
  const auth = useAuth();

  const handleLogin = async (values, { setSubmitting, setErrors, setTouched }) => {
    try {
      const resp = await auth.loginAction(values);
      if (resp.isValid) {
        navigate("/");
      } else {
        const { issueType, message } = resp.response;
        // const url = process.env.REACT_APP_CONTACT_SUPPORT_URL;
        // const clickableMessage = url
        //   ? (
        //       <span>
        //   <a href={url} target="_blank" rel="noopener noreferrer" style={{ marginRight: 8 }}>
        //     Contact Support
        //   </a>
        //   {message}
        //       </span>
        //     )
        //   : message;
        if (issueType === "email") {
          setErrors({ Email: message });
          setTouched({ Email: true }, false);
        } else if (issueType === "password") {
          setErrors({ Password: message });
          setTouched({ Password: true }, false);
        } else if (issueType === "password_setup_required") {
          await Swal.fire({
            title: "Finish Setting Up Your Account",
            text: message || "You need to set up your password before logging in.",
            icon: "info",
            confirmButtonText: "Request Setup Link",
            showCancelButton: true,
            cancelButtonText: "Stay Here",
            confirmButtonColor: "#2663EB",
          }).then((result) => {
            if (result.isConfirmed) {
              navigate(`/Forgot?email=${encodeURIComponent(values.Email)}`);
            }
          });
        } else if (issueType === "password_expired") {
          await Swal.fire({
            title: "Password Expired",
            text: message || "Your password has expired. Reset it to continue.",
            icon: "warning",
            confirmButtonText: "Reset Password",
            showCancelButton: true,
            cancelButtonText: "Cancel",
            confirmButtonColor: "#2663EB",
          }).then((result) => {
            if (result.isConfirmed) {
              navigate(`/Forgot?email=${encodeURIComponent(values.Email)}`);
            }
          });
        } else if (issueType === "locked") {
          await Swal.fire({
            title: "Account Locked",
            text: message || "Your account is temporarily locked. Please try again later.",
            icon: "warning",
            confirmButtonColor: "#2663EB",
          });
        } else {
          await Swal.fire({
            title: "Unable to log in",
            text: message || "Invalid credentials",
            icon: "error",
            confirmButtonColor: "#2663EB",
          });
        }
      }
    } catch (error) {
      console.error("Login Error:", error);
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <ConfigProvider
      theme={{
        token: {
          fontFamily: "'Figtree', sans-serif",
          borderRadius: 16,
          colorPrimary: "#2663EB",
        },
      }}
    >
      <div style={backgroundWrapper}>
        <Formik
          initialValues={{ Email: "", Password: "", staySignedIn: false }}
          validationSchema={loginSchema}
          onSubmit={handleLogin}
        >
          {({ values, errors, touched, handleChange, handleBlur, handleSubmit, isSubmitting, setFieldValue }) => (
            <div style={containerStyle}>

              <div style={{ marginBottom: "32px", textAlign: "center" }}>
                <img
                  src={`${process.env.PUBLIC_URL}/logo.png`}
                  style={{ width: "180px", marginBottom: "16px" }}
                  alt="Tissue Vault Logo"
                />
              </div>

              <Card bordered={false} style={cardStyle}>
                <div style={{ marginBottom: "32px" }}>
                  <Title level={2} style={{ margin: 0, fontWeight: 500}}>
                    Log In
                  </Title>
                  <Text type="secondary" style={{ fontSize: "15px" }}>
                    Welcome back! Please enter your details to log into Tissue Vault™.
                  </Text>
                </div>

                <Form layout="vertical" onFinish={handleSubmit} requiredMark={false}>
                  <Form.Item
                    label={<span style={labelStyle}>Email</span>}
                    validateStatus={touched.Email && errors.Email ? "error" : ""}
                    help={touched.Email && errors.Email}
                  >
                    <Input
                      name="Email"
                      prefix={<UserOutlined style={{ color: "#bfbfbf", marginRight: "4px" }} />}
                      placeholder="Enter your email"
                      size="large"
                      style={inputStyle}
                      value={values.Email}
                      onChange={handleChange}
                      onBlur={handleBlur}
                    />
                  </Form.Item>

                  <Form.Item
                    label={<span style={labelStyle}>Password</span>}
                    validateStatus={touched.Password && errors.Password ? "error" : ""}
                    help={touched.Password && errors.Password}
                  >
                    <Input.Password
                      name="Password"
                      prefix={<LockOutlined style={{ color: "#bfbfbf", marginRight: "4px" }} />}
                      placeholder="••••••••"
                      size="large"
                      style={inputStyle}
                      value={values.Password}
                      onChange={handleChange}
                      onBlur={handleBlur}
                    />
                  </Form.Item>

                  <div style={{ marginBottom: "20px" }}>
                    <Checkbox
                      checked={values.staySignedIn}
                      onChange={(event) => setFieldValue("staySignedIn", event.target.checked)}
                    >
                      Stay signed in for a week
                    </Checkbox>
                  </div>

                  <div style={{ textAlign: "right", marginBottom: "24px" }}>
                    <Link to="/Forgot" style={forgotPwdLink}>
                      Forgot Password?
                    </Link>
                  </div>

                  <Button
                    type="primary"
                    htmlType="submit"
                    loading={isSubmitting}
                    style={buttonStyle}
                    block
                  >
                    Log in
                  </Button>

                  <div style={{ textAlign: "center", marginBottom: "16px", marginTop: "16px" }}>
                    <a href={"https://www.tissuevault.health/support"} style={forgotPwdLink} target="_blank" rel="noopener noreferrer">
                      Contact Support
                    </a>
                  </div>
                </Form>
              </Card>

              <div style={footerLogoStyle}>
                <Text type="secondary" style={{ fontSize: "12px", marginRight: "8px" }}>
                  POWERED BY
                </Text>
                <img
                  src={`${process.env.PUBLIC_URL}/companyLogo.png`}
                  style={{ width: "120px", filter: "opacity(0.6)" }}
                  alt="Advanced Solution Logo"
                />
              </div>
            </div>
          )}
        </Formik>
      </div>
    </ConfigProvider>
  );
};

// --- Styles ---

const backgroundWrapper = {
  background: "#EDF3FF",
  minHeight: "100vh",
  fontFamily: "'Figtree', sans-serif",
};

const containerStyle = {
  display: "flex",
  flexDirection: "column",
  justifyContent: "center",
  alignItems: "center",
  minHeight: "100vh",
  padding: "24px",
};

const cardStyle = {
  width: "100%",
  maxWidth: "500px",
  borderRadius: "16px",
  boxShadow: "0 20px 25px -5px rgba(0, 0, 0, 0.1), 0 8px 10px -6px rgba(0, 0, 0, 0.1)",
  padding: "16px",
};

const labelStyle = {
  fontWeight: "600",
  fontSize: "14px",
};

const inputStyle = {
  height: "48px",
  borderRadius: "8px",
};

const buttonStyle = {
  height: "48px",
  borderRadius: "8px",
  fontSize: "16px",
  fontWeight: "600",
  marginTop: "8px",
};

const forgotPwdLink = {
  fontSize: "14px",
  fontWeight: "600",
};

const footerLogoStyle = {
  marginTop: "48px",
  display: "flex",
  alignItems: "center",
};

export default Login;
